import type {
  Careplan,
  DocumentFlow,
  HylyghtActivity,
  IQuestionnaire,
  QuestionnaireResponse,
} from '@healthblocks-io/core'
import type { Content } from '@/modules/content/types/content.type'

export type TaskType =
  | 'Remecare'
  | 'Questionnaire'
  | 'Hylyght'
  | 'Message'
  | 'Document'
  | 'Content'
  | 'FileUploadRequest'

export enum TaskStatus {
  Pending = 'pending',
  Completed = 'completed',
  Expired = 'expired',
}

export type BaseTask = {
  /**
   * Unique identifier of the task (starts with 'activity_{original_careplan_activity_id}' or 'document_{original_document_id}')
   */
  id: string

  /**
   * Type of the task
   */
  type: TaskType
  /**
   * Status of the task
   */
  status: TaskStatus

  /**
   * Display name of the task
   */
  display: string

  /**
   * Planned date of the task
   */
  plannedAt: Date

  /**
   * Expires date of the task
   */
  expiresAt: Date | null

  /**
   * Completed date of the task
   */
  completedAt: Date | null

  /**
   * Trigger of the task (unique identifier shared by multiple activities or document flows to relate to)
   */
  trigger?: string

  /**
   * When the task is recurring, it will be created again after it is completed
   */
  repeating?: boolean

  /**
   * When the task is recurring, it can not be completed until the next occurrence
   */
  recurring?: boolean

  /**
   * Careplan ID of the task (parent of the task)
   */
  careplanId?: number

  /**
   * Display name of the careplan
   */
  careplanDisplay?: string

  /**
   * Created date of the task
   */
  createdAt: Date

  /**
   * Updated date of the task
   */
  updatedAt: Date
}

export type TaskResponse = {
  data: Task[]
  page: number
  total: number
  limit: number
}

// ------------------------------------------------------------------------- /
// Union types
// ------------------------------------------------------------------------- /

/**
 * Union types (QuestionnaireTask, DocumentTask, RemecareTask, HylyghtTask, MessageTask, ContentTask)
 *
 */
export type QuestionnaireTask = BaseTask & {
  type: 'Questionnaire'
  driver: 'awell' | 'default'
  questionnaire?: IQuestionnaire
  questionnaireResponse: QuestionnaireResponse
  questionnaireId?: number
  questionnaireSlug?: string
  careplan?: Careplan
  message?: string
  careplanActivityId: number
  parentTaskId?: string
  parentCareplanActivityId?: number
  reconfirmation?: boolean
  prefill?: boolean
}

export type DocumentTask = BaseTask & {
  type: 'Document'
  documentId: number
  document: DocumentFlow
}

export type RemecareTask = BaseTask & {
  type: 'Remecare'
  remecareUrl: string
  careplanActivityId: number
}

export type HylyghtTask = BaseTask & {
  type: 'Hylyght'
  activity: HylyghtActivity
  careplanActivityId: number
}

export type MessageTask = BaseTask & {
  type: 'Message'
  /**
   * Title of the message
   */
  title?: string
  /**
   * Message of the task
   */
  message?: string
  /**
   * HTML body of the message
   */
  body?: string
  /**
   * Type of message (Action, Brochure, Document, or undefined for plain messages)
   */
  messageType?: string
  careplanActivityId: number
}

export type ContentTask = BaseTask & {
  type: 'Content'
  contentItem: Content
  message?: string
  careplanActivityId: number
}

export type TaskSubject = {
  projectId: number
  subjectId: number
}

export type FileUploadRequestTask = BaseTask & {
  type: 'FileUploadRequest'
  careplanActivityId: number
  meta: {
    description: string
    allowedTypes: Array<{ mimeType: string; maxSizeMb: number }>
    minFiles: number
    maxFiles: number
    fileIds?: string[]
  }
}

export type Task =
  | QuestionnaireTask
  | DocumentTask
  | RemecareTask
  | HylyghtTask
  | MessageTask
  | ContentTask
  | FileUploadRequestTask
